import React, { useEffect, useRef, useState } from "react";
import { Button, Spin, Upload } from "antd";
import { CheckCircleFilled, CloudUploadOutlined, FileTextOutlined } from "@ant-design/icons";

import { readClaimForm } from "../TreatmentPlans/component/shared/estimateClaimsApi";
import { describeApiError } from "../TreatmentPlans/lib/apiError";
import "../TreatmentPlans/component/shared/estimateClaims.css";
import { claimFormPatientPrefill, describeClaimForm } from "./claimForm";
import NewPatientForm from "./NewPatientForm";
import PatientPicker from "./PatientPicker";
import { formatDob } from "./newPatient";

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.tif,.tiff,.heic";

function nameOf(p) {
  return [p && p.first_name, p && p.last_name].filter(Boolean).join(" ") || "Unnamed patient";
}

/** What the reader is doing, by how long it has been at it — one request, so
 *  the honest progress is a description, not a percentage. */
function readingLine(seconds) {
  if (seconds < 2) return "Uploading the form…";
  if (seconds < 15) return "Reading the form — this takes up to 20 seconds…";
  return "Still reading — nearly there…";
}

/**
 * "Upload as File" on the Patient step: drop a claim form and the patient is
 * found — or drafted — from it.
 *
 * The read (estimateClaimsApi.readClaimForm) names the patient and lists the
 * office's patients this may be. One exact match (both names and the date of
 * birth) is selected for the user by the host, with "Not them?" to undo it.
 * Otherwise the possible matches are offered, and "add as a new patient" opens
 * NewPatientForm pre-filled with what the form said, to check and approve. A
 * file nothing could be read from (a logo, a blank page) is not the submission:
 * the search is offered instead, and the request asks for the details itself.
 *
 * The host owns the read (`claimForm`) and the chosen patient (`selected`).
 */
export default function ClaimFormUpload({ officeId, claimForm, onRead, onReplace, selected, onSelect }) {
  const [reading, setReading] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState("");
  // When no patient is chosen yet: "choose" from the matches, draft a "new"
  // one, or "search" by hand.
  const [mode, setMode] = useState("choose");
  const timer = useRef(null);

  useEffect(function () {
    return function () { if (timer.current) clearInterval(timer.current); };
  }, []);

  async function handleFile(file) {
    setError("");
    setReading(true);
    setSeconds(0);
    const started = Date.now();
    timer.current = setInterval(function () {
      setSeconds(Math.floor((Date.now() - started) / 1000));
    }, 500);
    let res = null;
    try {
      res = await readClaimForm(officeId, file);
    } finally {
      clearInterval(timer.current);
      timer.current = null;
      setReading(false);
    }
    if (!res || res.status >= 400 || !res.json || !res.json.claim_form_path) {
      setError(describeApiError(res, "Couldn't read that file. Try again, or find the patient instead."));
      return;
    }
    setMode("choose");
    onRead(res.json);
  }

  // --- Nothing uploaded yet ---------------------------------------------------
  if (!claimForm) {
    return (
      <div>
        <Spin spinning={reading} tip={readingLine(seconds)}>
          <Upload.Dragger
            multiple={false}
            showUploadList={false}
            accept={ACCEPT}
            disabled={reading}
            beforeUpload={function (file) {
              handleFile(file);
              return false;   // we upload it ourselves, to the read endpoint
            }}
            className="est-claim-drop"
          >
            <div className="est-claim-drop-icon"><CloudUploadOutlined /></div>
            <p className="est-claim-drop-text">Upload the claim form</p>
            <p className="est-claim-drop-hint">
              Drop a PDF or photo here, or <span>browse</span> — we'll read the patient, their
              insurance and the treatment from it.
            </p>
          </Upload.Dragger>
        </Spin>
        {reading ? (
          <p className="ir-hint" style={{ textAlign: "center" }}>
            We're reading the patient, their insurance and the treatment.
          </p>
        ) : null}
        {error ? <div className="ir-new-patient-error" role="alert" style={{ marginTop: 10 }}>{error}</div> : null}
      </div>
    );
  }

  const read = claimForm.patient || {};
  const candidates = claimForm.candidates || [];
  const carriers = (claimForm.policies || [])
    .map(function (p) { return p.carrier && p.carrier.name_raw; })
    .filter(Boolean);
  const readLine = [
    nameOf(read) !== "Unnamed patient" ? nameOf(read) : "",
    read.birth_date ? "Date of birth " + formatDob(read.birth_date) : "",
    carriers.join(" + "),
    describeClaimForm(claimForm),
  ].filter(Boolean).join(" · ");

  const fileChip = (
    <div className="est-claim-file">
      <FileTextOutlined />
      <span className="est-claim-file-name">{claimForm.file_name || "Claim form"}</span>
      <Button type="link" size="small" onClick={onReplace}>Replace</Button>
    </div>
  );

  // --- Read, and a patient is chosen --------------------------------------------
  if (selected) {
    const matched = candidates.some(function (c) { return c.id === selected.id; });
    return (
      <div>
        {fileChip}
        <div className="ir-chosen" style={{ marginTop: 12 }}>
          <CheckCircleFilled className="ir-chosen-tick" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ir-chosen-name">{nameOf(selected)}</div>
            <div className="ir-chosen-meta">
              {selected.birth_date ? "Date of birth " + formatDob(selected.birth_date) : "No date of birth on file"}
              {matched ? " · Found in Smilepass" : ""}
            </div>
          </div>
          <Button type="link" onClick={function () { onSelect(null); }} className="ir-chosen-change">
            {matched ? "Not them?" : "Change"}
          </Button>
        </div>
      </div>
    );
  }

  // --- Read, nothing usable on it: find them by hand ------------------------------
  if (claimForm.extraction_empty || mode === "search") {
    return (
      <div>
        {fileChip}
        <p className="ir-hint">
          {claimForm.extraction_empty
            ? "We couldn't find any patient or insurance details in this file. Find the patient below."
            : "Find the patient below — the form still goes with the request."}
        </p>
        <PatientPicker selected={null} onSelect={onSelect} />
        {!claimForm.extraction_empty ? (
          <p className="ir-add-patient-line">
            <button type="button" className="ir-link" onClick={function () { setMode("choose"); }}>
              Back to what the form says
            </button>
          </p>
        ) : null}
      </div>
    );
  }

  const prefill = claimFormPatientPrefill(read);

  // --- Read: offer the matches, or draft the patient from the form ----------------
  return (
    <div>
      {fileChip}
      {readLine ? (
        <div className="ir-claim-read">
          <span className="ir-claim-read-label">Read from the form</span> {readLine}
        </div>
      ) : null}

      {candidates.length && mode !== "new" ? (
        <div className="ir-search-panel">
          <div className="ir-panel-count">
            {candidates.some(function (c) { return c.match === "exact"; })
              ? "Already in Smilepass"
              : "Is this them?"}
          </div>
          {candidates.map(function (c) {
            return (
              <div key={c.id} className="ir-result" onClick={function () { onSelect(c); }}>
                <div>
                  <div className="ir-result-name">{nameOf(c)}</div>
                  <div className="ir-result-meta">
                    {c.birth_date ? formatDob(c.birth_date) : "No DOB"}
                    {c.phone ? " · " + c.phone : ""}
                    {c.from_pms ? " · From your PMS" : " · Added in Smilepass"}
                  </div>
                </div>
                <span className="ir-result-pick" style={{ opacity: 1 }}>Use this patient</span>
              </div>
            );
          })}
        </div>
      ) : (
        <NewPatientForm
          initial={prefill}
          title={candidates.length ? "Add them as a new patient" : "Not in Smilepass yet — add them"}
          intro={"Check what we read off the form and fix anything that's off, then add the patient. "
            + "Once your practice management system syncs, we'll link them to their record there."}
          onSelect={onSelect}
          onCancel={function () { setMode(candidates.length ? "choose" : "search"); }}
        />
      )}

      <p className="ir-add-patient-line">
        {candidates.length && mode !== "new" ? (
          <>
            None of these?{" "}
            <button type="button" className="ir-link" onClick={function () { setMode("new"); }}>
              Add as a new patient
            </button>
            {" · "}
          </>
        ) : null}
        <button type="button" className="ir-link" onClick={function () { setMode("search"); }}>
          Search instead
        </button>
      </p>
    </div>
  );
}
